/**
 * MfcLens - posts to clarity
 *
 *   const lens = MfcLens.getInstance(() => window['umami']);
 *
 *   lens.Login(sessionId, userId, username, accessLevel);
 *
 *   lens.Purchase_Success({ tokens: 200, amount: 19.99 }); // any event can carry ad-hoc fields
 */

class MfcLens {
    static _COMMON_EVENTS = [
        // signup
        'Signup_Click',
        'Signup_Submit',
        'Signup_Success',

        // purchase
        'Purchase_Click',
        'Purchase_Success',

        // pages
        'ChatRoom_Click',
        'ChatRoom_Send',
        'Homepage_Click',
        'PrivateMessage_Click',
        'PrivateMessage_Send',

        // actions
        'Club_Click',
        'Club_Join',
        'GoPrivate_Click',
        'GoPrivate_Join',
        'Group_Click',
        'Group_Join',
        'QuickTip_Click',
        'QuickTip_Submit',
        'Tip_Click',
        'Tip_Submit',
        'TipMenu_Click',
        'TipMenu_Submit',
        'Voyeur_Join',

        // features
        'FullScreen_Click',
        'InteractiveFullScreen_Click',
        'PinchZoom_Change',
        'TheaterMode_Click',

        // personal menu interaction
        'Bookmark_Add',
        'Bookmark_Remove',
        'Friend_Add',
        'Friend_Remove',
        'FriendsList_Click',
        'Hide_Add',
        'Hide_Remove',
        'Ignore_Add',
        'Ignore_Remove',

        // nav menu
        'Menu_Friends_Click',
        'Menu_Bookmarks_Click',
        'Menu_History_Click',
        'Menu_MissMFC_Click',
        'Menu_NewModels_Click',
        'Menu_Popular_Click',
        'Menu_Recommend_Click',
        'Menu_Tags_Click',
        'Menu_Topics_Click',
        'Menu_Trending_Click',
        'Menu_VisitedRooms_Click',
        'Menu_CustomSelection_Click',

        // member features
        'MemberRooms_Click',
        'MemberTags_Click',
        'MemberWebcams_Click',
        'MyWebcam_Click',
        'MyWebcam_Launch',

        // feature pages
        'Archives_Click',
        'ClubShows_Click',
        'CustomSelection_Click',
        'Explore_Click',
        'GroupShows_Click',
        'Lounge_Click',
        'ManageFriends_Click',
        'MfcMail_Click',
        'MissMFC_Click',
        'ModelMenu_Click',
        'MyAccount_Click',
        'NewModels_Click',
        'NewsFeed_Click',
        'Notes_Click',
        'Notes_Submit',
        'PrivateGallery_Click',
        'Profile_Click',
        'Recommend_Click',
        'Settings_Click',
        'Tags_Click',
        'Topics_Click',
        'Whiteboard_Click',

        // MFC.APP pages & nav
        'MessagesList_Click',
        'ModelBrowser_Click',
        'RoomsList_Click',
        'Menu_Click',
    ]

    suppressDuplicates = {}

    static _g() {
        if (typeof self !== 'undefined') return self
        if (typeof window !== 'undefined') return window
        if (typeof global !== 'undefined') return global
        throw new Error('no global object available')
    }

    constructor(
        client,
        {
            heartbeatMinMs = 180_000,
            heartbeatMaxMs = 300_000,
            heartbeat = true,
        } = {},
    ) {
        const existing = MfcLens._g()[MfcLens._key]
        if (existing) return existing

        this.client = client
        this.heartbeatMinMs = heartbeatMinMs
        this.heartbeatMaxMs = heartbeatMaxMs
        this._heartbeatTimer = null

        MfcLens._g()[MfcLens._key] = this
        if (heartbeat) this.startHeartbeat()
    }

    static getInstance(client, opts) {
        return MfcLens._g()[MfcLens._key] || new MfcLens(client, opts)
    }

    static reset() {
        const existing = MfcLens._g()[MfcLens._key]
        if (existing) existing.stopHeartbeat()
        delete MfcLens._g()[MfcLens._key]
    }

    _resolve() {
        return typeof this.client === 'function' ? this.client() : this.client
    }

    track(name, data) {
        if (name && this.suppressDuplicates[name]) {
            console.log('Suppressed duplicate track of ' + name)
            return
        }
        if (
            process.env.NODE_ENV === 'development' ||
            window?.location.host.includes('dev')
        ) {
            console.log('tracking', name, 'with', data)
        }
        setTimeout(() => {
            delete this.suppressDuplicates[name]
        }, 250)
        this.suppressDuplicates[name] = 1

        const c = this._resolve()
        if (!c) return
        const r =
            data && Object.keys(data).length
                ? c.track(name, data)
                : c.track(name)
        // push the fallback heartbeat out
        this._beat()
        return r
    }

    identify(id, data) {
        const c = this._resolve()
        if (!c) return
        return data ? c.identify(id, data) : c.identify(id)
    }

    // --- Events ------------------------------------------------------------
    // Convention: required fields are named params, optional `extra` is always
    // last and spread into the payload. Method name === wire name, minus CustomTrack()

    Login(sessionId, userId, username, accessLevel, extra = {}) {
        const data = { sessionId, userId, username, accessLevel, ...extra }
        if (userId > 0) {
            this.identify(String(userId), data)
        } else {
            this.identify(data)
        }
        return this.track('Login', data)
    }

    // Custom (maybe add it above if it's not a one-off?)
    CustomEvent(eventName, extra = {}) {
        return this.track(eventName, { ...extra })
    }

    // --- Heartbeat ---------------------------------------------------------

    _heartbeatDelay() {
        const min = this.heartbeatMinMs
        const max = this.heartbeatMaxMs
        return Math.floor(Math.random() * (max - min + 1)) + min
    }

    // Rearm the fallback timer. Any send calls this, so the heartbeat only
    // fires after a full interval of silence.
    _beat() {
        if (this._heartbeatTimer !== null) clearTimeout(this._heartbeatTimer)
        this._heartbeatTimer = setTimeout(
            () => this.track('heartbeat'),
            this._heartbeatDelay(),
        )
    }

    startHeartbeat() {
        if (this._heartbeatTimer === null) this._beat()
    }

    stopHeartbeat() {
        if (this._heartbeatTimer !== null) {
            clearTimeout(this._heartbeatTimer)
            this._heartbeatTimer = null
        }
    }
}

for (const name of MfcLens._COMMON_EVENTS) {
    MfcLens.prototype[name] = function (extra = {}) {
        return this.track(name, { ...extra })
    }
}

MfcLens._key = Symbol.for('mfclens.instance')

export default MfcLens
